import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { unsubscribeToken } from '../../lib/notifications.js';

/**
 * <community-unsubscribe> — where an email's "stop emails like this" link
 * lands.
 *
 * Public, like the legal pages: the link has to work on a device where nobody
 * is signed in. The token in the link is the authorisation, and all it can do
 * is switch one kind of email off.
 *
 * It waits for a click rather than unsubscribing on load. Mail security
 * scanners open links to check them, and some run the page's script while they
 * do; a page that acted on load would unsubscribe people nobody asked to.
 * A mail client's own unsubscribe button does not come here at all — it POSTs
 * to the API directly (RFC 8058).
 */
export class CommunityUnsubscribe extends BaseComponent {
    static properties = {
        _token: { state: true },
        _working: { state: true },
        _done: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        this._token = unsubscribeToken(window.location.search);
        this._working = false;
        /** @type {{category: string, label: string}|null} */
        this._done = null;
        this._error = '';
    }

    async _unsubscribe() {
        this._working = true;
        this._error = '';
        try {
            this._done = await api.post(`/unsubscribe?token=${encodeURIComponent(this._token)}`);
        } catch (err) {
            this._error =
                /** @type {any} */ (err).status === 400
                    ? "This unsubscribe link isn't valid. You can change what you're emailed from your email settings once you sign in."
                    : String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._working = false;
        }
    }

    _renderBody() {
        if (!this._token) {
            return html`<p class="mb-0">
                This page needs the link from a notification email. You can change what you're
                emailed from your <a href="/settings">email settings</a> once you sign in.
            </p>`;
        }

        if (this._done) {
            return html`
                <p>
                    Done. You won't be emailed about
                    <strong>${this._done.label.toLowerCase()}</strong> any more.
                </p>
                <p class="text-muted small mb-0">
                    You'll still see these under Notifications in the portal. Change your mind in
                    your <a href="/settings">email settings</a>.
                </p>
            `;
        }

        return html`
            <p>Stop emails like the one you just followed this link from?</p>
            ${this._error ? html`<div class="alert alert-warning small">${this._error}</div>` : nothing}
            <button class="btn btn-primary" ?disabled=${this._working} @click=${this._unsubscribe}>
                Unsubscribe
            </button>
        `;
    }

    render() {
        return html`
            <div class="container py-4" style="max-width: 36rem;">
                <div class="card shadow-sm border-0">
                    <div class="card-body p-4">
                        <h1 class="h4 mb-3">Notification email</h1>
                        ${this._renderBody()}
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('community-unsubscribe', CommunityUnsubscribe);
